import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LegacyShimsModule } from 'apps/account/src/account-shared/legacy-shims/legacy-shims.module';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { SOCIAL_NETWORKS } from '../../../../account-v2/src/app/api/socialNetwork';
import { ShareType } from '../../../../account-v2/src/app/components/broadcast/shareType';
import { StoryDescriptor } from '../../../../account-v2/src/app/components/broadcast/storyDescriptor';
import { BroadcastFlowBase, BroadcastFlowStep } from './broadcast-flow-base';
import { FtBroadcastChooseNetworkComponent } from './ft-broadcast-choose-network.component';
import { FtBroadcastShareImageReadyComponent } from './ft-broadcast-share-image-ready.component';
import { FtStoryImageSetupComponent } from './ft-story-image-setup.component';

/**
 * Broadcast-from-recommendation modal flow. ShareImageReady starts active when
 * the recommendation carries a pre-built share image; otherwise init advances to
 * ChooseNetwork. Standalone — mounted via createComponent.
 */
@Component({
    selector: 'ft-broadcast-recommended-story-modal-ng',
    imports: [
        TranslateModule,
        LegacyShimsModule,
        FtModalComponent,
        FtModalChromeComponent,
        FtBroadcastChooseNetworkComponent,
        FtBroadcastShareImageReadyComponent,
        FtStoryImageSetupComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-broadcast-recommended-story-modal.component.html',
})
export class FtBroadcastRecommendedStoryModalComponent extends BroadcastFlowBase implements OnInit, OnDestroy {
    // Stays a decorator @Input: it overrides the base class's PLAIN `recommendation`
    // field (read as `this.recommendation` throughout BroadcastFlowBase) — a signal
    // input here would break those base reads. setInput() works with both forms.
    @Input() override recommendation: any;

    /** v1 derived-class redefinition (same value, kept for verbatim mapping). */
    static readonly STEP_NAME_IMAGE_SETUP = 'ImageSetup';

    ngOnInit(): void {
        this.steps = [
            new BroadcastFlowStep('ShareImageReady', true),
            new BroadcastFlowStep('CreateImageProgress'),
            new BroadcastFlowStep('StoryShareImageSetup'),
            new BroadcastFlowStep('PhotocontestInvitationShareImageSetup'),
            new BroadcastFlowStep('ChooseNetwork'),
        ];

        document.body.classList.add('modal-open', 'modal-with-am-fade');

        // Verbatim BroadcastStoryFromRecommendationFlowController ctor body.
        this.property = this.sessionService.getProperty(this.propertyUuid());
        this.fullContext = `/undefined/${this.spacesService.getPropertyUuid()}`;

        Promise.all([
            this.photoContest.details(this.propertyUuid(), this.recommendation.recommendation_data.campaign_promoter_uuid),
            this.layersApi.photocontest({ propertyId: this.propertyUuid() }),
        ]).then((datas: any[]) => {
            this.zone.run(() => {
                const story = datas[0],
                    photoContestLayer = datas[1];

                this.setShareType(BroadcastFlowBase.RecommendationTypeToShareTypeMap[this.recommendation.recommendation_type_id]);

                this.contests = this.parse.arrayOrDefault(photoContestLayer.contests);

                Promise.all([
                    !!story.guest.poststay.photo
                        ? this.photoContest.load({ contestId: story.guest.poststay.photo.photoContest.uuid, propertyUuid: this.propertyUuid() })
                        : null,
                    this.shareType == ShareType.PhotoContestInvitation
                        ? this.photoContest.load({ contestId: this.recommendation.custom_data.contestUuid, propertyUuid: this.propertyUuid() })
                        : null,
                ]).then((inner: any[]) => {
                    this.zone.run(() => {
                        this.contest = inner[0];
                        this.inviteToContest = inner[1];
                        this.story = story;
                        this.storyDescriptor = new StoryDescriptor(this.parse, story);
                        this.isInitialized = true;

                        // create bunch of models for components
                        this.shareImageUrl = this.recommendation.custom_data.shareImageUrl ? this.recommendation.custom_data.shareImageUrl : null;
                        this.imageSetupOptions = this.recommendation.custom_data.imageSetupOptions;
                        this.storySharedTo = story.socialNetworksSharing
                            .filter((n: any) => this.parse.intOrDefault(n.totalShareTimes) > 0)
                            .map((n: any) => this.parse.intOrDefault(n.socialNetworkType));

                        const isImageReady = !!this.shareImageUrl;
                        if (isImageReady) {
                            this.network = SOCIAL_NETWORKS.map((n) =>
                                Object.assign({}, n, { isSharedTo: !!this.storySharedTo.find((s: any) => s == n.type) }),
                            ).find((n) => n.type == this.recommendation.custom_data.socialNetworkType);
                            this.shareUrl = this.getShareUrl();
                        } else {
                            this.moveTo(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK);
                        }
                    });
                });
            });
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('modal-open', 'modal-with-am-fade');
    }

    returnToImageSetup(): void {
        this.moveTo(
            this.shareType == ShareType.Story
                ? BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP
                : BroadcastFlowBase.STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP,
        );
    }
}
